// ============================================================================
// ROUTINES API — React Query hooks for /api/v1/routines/
// Routines are scheduled Flow runs. This client mirrors the backend
// ai_agent.routines.serialize_routine shape, which the chat RoutineCard's
// RoutineSpec (chatRenders.ts) also consumes.
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'

export interface Routine {
  id: number
  name: string
  description: string
  prompt: string
  cadence: 'daily' | 'weekly' | 'monthly' | 'once'
  /** Human-words render from the backend, e.g. "Sundays at 18:00 (Europe/Berlin)". Never cron syntax. */
  schedule: string
  time_of_day: string  // "HH:MM"
  day_of_week: number | null
  day_of_month: number | null
  timezone: string
  enabled: boolean
  next_run_at: string | null
  /** Fire instant for cadence='once' (null otherwise). */
  fire_at: string | null
  last_run_at: string | null
  /** The conversation the last run produced, or null before the first run.
   *  Deep-links as /chat?conversation=<id> — the routine's own run, not the
   *  chat page the user happened to leave open. */
  last_run_conversation_id: number | null
  /** WHOSE data the run reads. 'self' = the owner's own. The UI must show this:
   *  the reported bug was a coach unable to tell that a routine they had made
   *  was watching an athlete rather than the coach. */
  subject_scope: 'self' | 'athlete' | 'group'
  subject_profile_id: number | null
  subject_group_uuid: string | null
  /** Who the run reports to. Against an athlete, the list also holds the
   *  athlete's own routines, which are not the viewer's to run or open. */
  owner_profile_id: number
}

export interface RoutineInput {
  name: string
  prompt: string
  cadence: Routine['cadence']
  time_of_day?: string | null
  day_of_week?: number | null
  day_of_month?: number | null
  timezone?: string
  description?: string
  enabled?: boolean
  /** cadence='once' only: ISO datetime of the single future run. */
  fire_at?: string | null
}

const QUERY_KEY = (profileId?: number | null) =>
  profileId ? ['routines', profileId] : ['routines']

export function useRoutines(targetProfileId?: number | null) {
  return useQuery<Routine[]>({
    queryKey: QUERY_KEY(targetProfileId),
    queryFn: async () => {
      const { data } = await apiClient.get<Routine[]>('/routines/', {
        params: targetProfileId ? { target_profile_id: targetProfileId } : undefined,
      })
      return data
    },
  })
}

export function useUpdateRoutine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...fields }: Partial<RoutineInput> & { id: number }) => {
      const { data } = await apiClient.patch<Routine>(`/routines/${id}/`, fields)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routines'] }),
  })
}

export function useDeleteRoutine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/routines/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routines'] }),
  })
}

export function useRunRoutine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post<{ success: boolean; routine_id: number }>(`/routines/${id}/run/`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routines'] }),
  })
}
